import React, { useEffect, useMemo, useState } from "react";
import { Box } from "@mui/material";
import { alpha } from "@mui/material/styles";
import {
  addEdge,
  applyEdgeChanges,
  applyNodeChanges,
  type Connection,
  type EdgeChange,
  Handle,
  MarkerType,
  type NodeChange,
  OnInit,
  Position,
  ReactFlow,
  type Edge,
  type Node,
  type NodeProps,
  type ReactFlowInstance,
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import type { BusinessService, Status } from "../types";
import { COLORS, palette } from "../theme/theme-options";

const statusColors: Record<Status, { main: string; subtle: string }> = {
  healthy: { main: "#22c55e", subtle: "rgba(34,197,94,0.15)" },
  warning: { main: "#f59e0b", subtle: "rgba(245,158,11,0.15)" },
  critical: { main: "#ef4444", subtle: "rgba(239,68,68,0.15)" },
};

type BusinessServiceNode = Node<BusinessService>;
type ServiceMapEdge = Edge;
type ServiceMapDiagramVariant = "focused" | "full";
type ServiceMapDiagramMode = "viewer" | "editor";
type NodeClickContext = {
  left: number;
  top: number;
  anchorEl?: HTMLElement | null;
};

const NODE_WIDTH = 220;
const NODE_HEIGHT = 170;
const CIRCLE_SIZE = 88;
const CIRCLE_TOP = 8;
const DIAGRAM_PADDING_Y = 24;
const DIAGRAM_MIN_HEIGHT = NODE_HEIGHT + DIAGRAM_PADDING_Y * 2;
const ROW_VERTICAL_GAP = 250;
const COLUMN_HORIZONTAL_GAP = NODE_WIDTH + 76;
const FIT_VIEW_OPTIONS = { padding: 0.12, duration: 350, minZoom: 0.55, maxZoom: 1.15 };

type ServiceNodeExtraProps = {
  rootNodeId?: string;
  hoveredNodeId: string | null;
  connectedNodeIds: Set<string>;
  mode: ServiceMapDiagramMode;
  onNodeActionClick?: (nodeId: string, clickContext: NodeClickContext) => void;
};

const scoreFromStatus = (status: Status): number => {
  if (status === "healthy") {
    return 92;
  }
  if (status === "warning") {
    return 74;
  }
  return 64;
};

const truncateLabel = (value: string, max = 27): string => {
  if (value.length <= max) {
    return value;
  }
  return `${value.slice(0, max - 3)}...`;
};

const filterEdgesForKnownNodes = (
  nodes: BusinessServiceNode[],
  edges: ServiceMapEdge[],
): ServiceMapEdge[] => {
  const nodeIds = new Set(nodes.map((node) => node.id));
  return edges.filter(
    (edge) => nodeIds.has(edge.source) && nodeIds.has(edge.target),
  );
};

const ServiceNode = ({
  data,
  id,
  selected,
  rootNodeId,
  hoveredNodeId,
  connectedNodeIds,
  mode,
  onNodeActionClick,
}: NodeProps<BusinessServiceNode> & ServiceNodeExtraProps) => {
  const c = statusColors[data.status];
  const isRoot = id === rootNodeId;
  const score = Math.round(data.metrics?.score ?? scoreFromStatus(data.status));
  const kpis = data.kpis.length;
  const isHovered = hoveredNodeId === id;
  const isConnectedToHovered = hoveredNodeId !== null && connectedNodeIds.has(id);
  const isEditor = mode === "editor";
  const nodeOpacity = isEditor
    ? 1
    : hoveredNodeId === null
      ? 1
      : isHovered || isConnectedToHovered
        ? 1
        : 0.25;
  const handleStyle = {
    opacity: isEditor ? 1 : 0,
    width: isEditor ? 9 : 8,
    height: isEditor ? 9 : 8,
    background: isEditor ? alpha(COLORS.primary[300], 0.9) : "transparent",
    border: isEditor ? `1px solid ${alpha(COLORS.grey[900], 0.8)}` : "none",
  };

  return (
    <Box
      sx={{
        width: NODE_WIDTH,
        height: NODE_HEIGHT,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "flex-start",
        textAlign: "center",
        position: "relative",
        pt: `${CIRCLE_TOP}px`,
        opacity: nodeOpacity,
        transition: "opacity 140ms ease",
        ":hover": { cursor: "pointer" },
        "&:hover .service-map-node-circle": {
          transform: "translateY(-2px) scale(1.02)",
          filter: `drop-shadow(0 0 12px ${alpha(c.main, 0.35)})`,
        },
        "&:hover .service-map-node-label": {
          color: COLORS.grey[100],
        },
      }}
      onClick={(event) => {
        if (!isEditor || !onNodeActionClick) {
          return;
        }
        const currentTarget = event.currentTarget;
        const rect = currentTarget.getBoundingClientRect();
        onNodeActionClick(id, {
          left: event.clientX || rect.left + rect.width / 2,
          top: event.clientY || rect.top + rect.height / 2,
          anchorEl: currentTarget,
        });
      }}
    >
      <Handle
        id="target-top"
        type="target"
        position={Position.Top}
        style={{ ...handleStyle, top: CIRCLE_TOP }}
      />
      <Handle
        id="source-top"
        type="source"
        position={Position.Top}
        style={{ ...handleStyle, top: CIRCLE_TOP }}
      />
      <Handle
        id="target-bottom"
        type="target"
        position={Position.Bottom}
        style={{
          ...handleStyle,
          bottom: NODE_HEIGHT - (CIRCLE_TOP + CIRCLE_SIZE),
        }}
      />
      <Handle
        id="source-bottom"
        type="source"
        position={Position.Bottom}
        style={{
          ...handleStyle,
          bottom: NODE_HEIGHT - (CIRCLE_TOP + CIRCLE_SIZE),
        }}
      />

      <Box
        className="service-map-node-circle"
        sx={{
          width: CIRCLE_SIZE,
          height: CIRCLE_SIZE,
          borderRadius: "50%",
          border: `2px solid ${c.main}`,
          background: `radial-gradient(circle at 30% 20%, ${alpha(COLORS.grey[800], 0.75)}, ${alpha(COLORS.grey[900], 0.96)})`,
          boxShadow: selected
            ? `0 0 0 4px ${c.subtle}`
            : isRoot
              ? `0 0 0 4px ${alpha(COLORS.primary[400], 0.22)}`
              : isHovered
                ? `0 0 0 6px ${alpha(c.main, 0.16)}`
                : isEditor
                  ? `0 0 0 2px ${alpha(COLORS.primary[400], 0.12)}`
                  : "none",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          position: "relative",
          transition: "transform 160ms ease, filter 160ms ease, box-shadow 160ms ease",
          "::before": isRoot
            ? {
                content: "\"\"",
                position: "absolute",
                inset: -7,
                borderRadius: "50%",
                border: `1px dashed ${alpha(c.main, 0.65)}`,
              }
            : undefined,
        }}
      >
        <Box sx={{ color: c.main, fontSize: 34, fontWeight: 750, lineHeight: 1 }}>{score}</Box>
        <Box sx={{ color: COLORS.grey[500], fontSize: 11, lineHeight: 1.1 }}>{kpis} KPIs</Box>

      </Box>

      <Box
        className="service-map-node-label"
        sx={{
          mt: 1.2,
          color: isRoot ? palette.text.primary : COLORS.grey[300],
          fontSize: 14,
          fontWeight: 650,
          maxWidth: NODE_WIDTH,
          px: 1,
          transition: "color 160ms ease",
        }}
      >
        {truncateLabel(data.label)}
      </Box>
      <Box sx={{ mt: 0.2, color: COLORS.grey[500], fontSize: 12 }}>{data.owner}</Box>
    </Box>
  );
};

// Returns only the root and its direct incoming/outgoing links with adjacent nodes.
const buildConnectedSubgraph = (
  rootNodeId: string,
  nodes: BusinessServiceNode[],
  edges: ServiceMapEdge[],
): { nodes: BusinessServiceNode[]; edges: ServiceMapEdge[] } => {
  const byId = new Map(nodes.map((n) => [n.id, n]));
  if (!byId.has(rootNodeId)) {
    return { nodes: [], edges: [] };
  }

  const nodeIds = new Set<string>([rootNodeId]);
  const directEdges: ServiceMapEdge[] = [];

  for (const edge of edges) {
    const touchesRoot = edge.source === rootNodeId || edge.target === rootNodeId;
    if (!touchesRoot) {
      continue;
    }

    if (!byId.has(edge.source) || !byId.has(edge.target)) {
      continue;
    }

    directEdges.push(edge);
    nodeIds.add(edge.source);
    nodeIds.add(edge.target);
  }

  return {
    nodes: Array.from(nodeIds).map((id) => byId.get(id)).filter((n): n is BusinessServiceNode => Boolean(n)),
    edges: directEdges,
  };
};

// Layout with root centered, dependents above, dependencies below.
const layoutAroundRoot = (
  rootNodeId: string,
  nodes: BusinessServiceNode[],
  edges: ServiceMapEdge[],
): BusinessServiceNode[] => {
  const byId = new Map(nodes.map((node) => [node.id, node]));
  const dependencyIds = new Set<string>();
  const dependentIds = new Set<string>();

  for (const edge of edges) {
    if (edge.target === rootNodeId && byId.has(edge.source)) {
      dependencyIds.add(edge.source);
    }
    if (edge.source === rootNodeId && byId.has(edge.target)) {
      dependentIds.add(edge.target);
    }
  }

  for (const dependencyId of dependencyIds) {
    dependentIds.delete(dependencyId);
  }

  const orderByLabel = (a: string, b: string): number => {
    const aLabel = byId.get(a)?.data.label ?? a;
    const bLabel = byId.get(b)?.data.label ?? b;
    return aLabel.localeCompare(bLabel);
  };

  const positionedById = new Map<string, { x: number; y: number }>();

  const placeRow = (ids: string[], centerY: number) => {
    const sortedIds = [...ids].sort(orderByLabel);
    if (sortedIds.length === 0) {
      return;
    }
    const rowWidth = (sortedIds.length - 1) * COLUMN_HORIZONTAL_GAP;
    const startX = -rowWidth / 2;

    for (const [index, id] of sortedIds.entries()) {
      const centerX = startX + index * COLUMN_HORIZONTAL_GAP;
      positionedById.set(id, {
        x: centerX - NODE_WIDTH / 2,
        y: centerY - NODE_HEIGHT / 2,
      });
    }
  };

  positionedById.set(rootNodeId, {
    x: -NODE_WIDTH / 2,
    y: -NODE_HEIGHT / 2,
  });
  placeRow(Array.from(dependentIds), -ROW_VERTICAL_GAP);
  placeRow(Array.from(dependencyIds), ROW_VERTICAL_GAP);

  const unplacedIds = nodes
    .map((node) => node.id)
    .filter((id) => !positionedById.has(id))
    .sort(orderByLabel);
  placeRow(unplacedIds, ROW_VERTICAL_GAP * 1.8);

  return nodes.map((node) => {
    const position = positionedById.get(node.id) ?? { x: -NODE_WIDTH / 2, y: -NODE_HEIGHT / 2 };
    return {
      ...node,
      type: "ServiceNode",
      position,
      sourcePosition: Position.Bottom,
      targetPosition: Position.Top,
    };
  });
};

// Layout the entire graph in layers from upstream to downstream services.
const layoutFullGraph = (
  nodes: BusinessServiceNode[],
  edges: ServiceMapEdge[],
): BusinessServiceNode[] => {
  if (nodes.length === 0) {
    return [];
  }

  const byId = new Map(nodes.map((node) => [node.id, node]));
  const outgoingById = new Map<string, string[]>();
  const indegreeById = new Map<string, number>();

  for (const node of nodes) {
    outgoingById.set(node.id, []);
    indegreeById.set(node.id, 0);
  }

  for (const edge of edges) {
    if (!byId.has(edge.source) || !byId.has(edge.target)) {
      continue;
    }
    outgoingById.get(edge.source)?.push(edge.target);
    indegreeById.set(
      edge.target,
      (indegreeById.get(edge.target) ?? 0) + 1,
    );
  }

  const orderByLabel = (a: string, b: string): number => {
    const aLabel = byId.get(a)?.data.label ?? a;
    const bLabel = byId.get(b)?.data.label ?? b;
    return aLabel.localeCompare(bLabel);
  };

  const levelById = new Map<string, number>();
  const indegreeWork = new Map(indegreeById);
  const queue: string[] = [];

  for (const [nodeId, indegree] of indegreeById.entries()) {
    if (indegree === 0) {
      levelById.set(nodeId, 0);
      queue.push(nodeId);
    }
  }

  if (queue.length === 0) {
    const seedNode = [...byId.keys()].sort(orderByLabel)[0];
    if (seedNode) {
      queue.push(seedNode);
      levelById.set(seedNode, 0);
    }
  }

  const processed = new Set<string>();

  while (queue.length > 0) {
    const nodeId = queue.shift();
    if (!nodeId || processed.has(nodeId)) {
      continue;
    }
    processed.add(nodeId);
    const currentLevel = levelById.get(nodeId) ?? 0;
    const outgoing = outgoingById.get(nodeId) ?? [];

    for (const targetId of outgoing) {
      const nextLevel = currentLevel + 1;
      const previousLevel = levelById.get(targetId) ?? 0;
      if (nextLevel > previousLevel) {
        levelById.set(targetId, nextLevel);
      }

      const nextIndegree = (indegreeWork.get(targetId) ?? 0) - 1;
      indegreeWork.set(targetId, nextIndegree);
      if (nextIndegree === 0) {
        queue.push(targetId);
      }
    }
  }

  const unresolvedIds = [...byId.keys()]
    .filter((nodeId) => !processed.has(nodeId))
    .sort(orderByLabel);
  if (unresolvedIds.length > 0) {
    const maxAssignedLevel =
      levelById.size > 0
        ? Math.max(...Array.from(levelById.values()))
        : 0;
    unresolvedIds.forEach((nodeId, idx) => {
      levelById.set(nodeId, maxAssignedLevel + 1 + Math.floor(idx / 4));
    });
  }

  const maxLevel =
    levelById.size > 0 ? Math.max(...Array.from(levelById.values())) : 0;
  const centerYOffset = (maxLevel * ROW_VERTICAL_GAP) / 2;
  const nodePositionById = new Map<string, { x: number; y: number }>();
  const nodeIdsByLevel = new Map<number, string[]>();

  for (const node of nodes) {
    const level = levelById.get(node.id) ?? 0;
    const row = nodeIdsByLevel.get(level) ?? [];
    row.push(node.id);
    nodeIdsByLevel.set(level, row);
  }

  for (const [level, nodeIds] of [...nodeIdsByLevel.entries()].sort(
    (a, b) => a[0] - b[0],
  )) {
    const sortedIds = [...nodeIds].sort(orderByLabel);
    const rowWidth = (sortedIds.length - 1) * COLUMN_HORIZONTAL_GAP;
    const startX = -rowWidth / 2;
    const centerY = level * ROW_VERTICAL_GAP - centerYOffset;

    for (const [index, nodeId] of sortedIds.entries()) {
      const centerX = startX + index * COLUMN_HORIZONTAL_GAP;
      nodePositionById.set(nodeId, {
        x: centerX - NODE_WIDTH / 2,
        y: centerY - NODE_HEIGHT / 2,
      });
    }
  }

  return nodes.map((node) => ({
    ...node,
    type: "ServiceNode",
    sourcePosition: Position.Bottom,
    targetPosition: Position.Top,
    position: nodePositionById.get(node.id) ?? {
      x: -NODE_WIDTH / 2,
      y: -NODE_HEIGHT / 2,
    },
  }));
};

const withDiagramNodeDefaults = (
  nodes: BusinessServiceNode[],
): BusinessServiceNode[] => {
  return nodes.map((node) => ({
    ...node,
    type: "ServiceNode",
    sourcePosition: Position.Bottom,
    targetPosition: Position.Top,
  }));
};

const buildEdgeId = (source: string, target: string): string => `${source}#${target}`;

const serializeDiagramSnapshot = (
  nodes: BusinessServiceNode[],
  edges: ServiceMapEdge[],
): string => {
  const compactNodes = [...nodes]
    .map((node) => ({
      id: node.id,
      x: Number(node.position.x.toFixed(2)),
      y: Number(node.position.y.toFixed(2)),
    }))
    .sort((a, b) => a.id.localeCompare(b.id));
  const compactEdges = [...edges]
    .map((edge) => ({
      id: edge.id,
      source: edge.source,
      target: edge.target,
    }))
    .sort((a, b) => a.id.localeCompare(b.id));
  return JSON.stringify({ nodes: compactNodes, edges: compactEdges });
};

const normalizeEditableEdges = (
  edges: ServiceMapEdge[],
  nodes: BusinessServiceNode[],
): ServiceMapEdge[] => {
  const nodeIds = new Set(nodes.map((node) => node.id));
  const uniqueById = new Map<string, ServiceMapEdge>();

  for (const edge of edges) {
    if (
      !edge.source ||
      !edge.target ||
      edge.source === edge.target ||
      !nodeIds.has(edge.source) ||
      !nodeIds.has(edge.target)
    ) {
      continue;
    }
    const id = buildEdgeId(edge.source, edge.target);
    uniqueById.set(id, {
      ...edge,
      id,
      source: edge.source,
      target: edge.target,
    });
  }

  return Array.from(uniqueById.values());
};

const getClientPointFromEvent = (
  event: unknown,
): { left: number; top: number } | null => {
  if (!event || typeof event !== "object") {
    return null;
  }

  const maybeMouseEvent = event as { clientX?: number; clientY?: number };
  if (
    typeof maybeMouseEvent.clientX === "number" &&
    typeof maybeMouseEvent.clientY === "number"
  ) {
    return {
      left: maybeMouseEvent.clientX,
      top: maybeMouseEvent.clientY,
    };
  }

  const maybeTouchEvent = event as {
    touches?: Array<{ clientX: number; clientY: number }>;
    changedTouches?: Array<{ clientX: number; clientY: number }>;
  };
  const touchPoint =
    maybeTouchEvent.touches?.[0] ?? maybeTouchEvent.changedTouches?.[0];
  if (touchPoint) {
    return {
      left: touchPoint.clientX,
      top: touchPoint.clientY,
    };
  }

  const maybeCurrentTarget = event as { currentTarget?: EventTarget | null };
  if (maybeCurrentTarget.currentTarget instanceof HTMLElement) {
    const rect = maybeCurrentTarget.currentTarget.getBoundingClientRect();
    return {
      left: rect.left + rect.width / 2,
      top: rect.top + rect.height / 2,
    };
  }

  return null;
};

// Sets edge handles, direction, and opacity; highlights only edges connected to the hovered node.
const styleEdges = (
  edges: ServiceMapEdge[],
  nodes: BusinessServiceNode[],
  hoveredNodeId: string | null,
  focusedEdgeId: string | null,
  mode: ServiceMapDiagramMode,
): ServiceMapEdge[] => {
  const posById = new Map(nodes.map((node) => [node.id, node.position]));
  const isEditor = mode === "editor";

  return edges.map((edge) => {
    const source = posById.get(edge.source);
    const target = posById.get(edge.target);

    let sourceHandle = "source-bottom";
    let targetHandle = "target-top";
    let dashed = false;

    if (!isEditor && source && target) {
      const dy = target.y - source.y;

      if (dy >= 0) {
        sourceHandle = "source-bottom";
        targetHandle = "target-top";
      } else {
        sourceHandle = "source-top";
        targetHandle = "target-bottom";
        dashed = true;
      }
    }

    const isConnectedToHovered =
      hoveredNodeId !== null && (edge.source === hoveredNodeId || edge.target === hoveredNodeId);
    const isFocusedEdge = focusedEdgeId === edge.id;
    const edgeOpacity = isEditor
      ? isFocusedEdge ? 1 : 0.9
      : hoveredNodeId === null
        ? isFocusedEdge ? 1 : 0.28
        : isConnectedToHovered
          ? 1
          : 0.1;
    const strokeColor = isFocusedEdge
      ? COLORS.primary[300]
      : alpha(COLORS.grey[300], 0.85);
    const markerColor = isFocusedEdge
      ? COLORS.primary[300]
      : alpha(COLORS.grey[300], 0.9);

    return {
      ...edge,
      type: "smoothstep",
      sourceHandle,
      targetHandle,
      markerEnd: {
        type: MarkerType.ArrowClosed,
        width: 18,
        height: 18,
        color: markerColor,
      },
      style: {
        stroke: strokeColor,
        strokeWidth: isFocusedEdge ? (isEditor ? 2.9 : 2.5) : isEditor ? 2.1 : 1.8,
        strokeDasharray: dashed ? "6 4" : undefined,
        opacity: edgeOpacity,
      },
    };
  });
};

export type ServiceMapDiagramProps = {
  nodes: BusinessServiceNode[];
  edges: ServiceMapEdge[];
  rootNodeId?: string;
  variant?: ServiceMapDiagramVariant;
  mode?: ServiceMapDiagramMode;
  interactive?: boolean;
  preservePositions?: boolean;
  height?: number | string;
  onMapChange?: (map: {
    nodes: BusinessServiceNode[];
    edges: ServiceMapEdge[];
  }) => void;
  onNodeClick?: (nodeId: string, clickContext?: NodeClickContext) => void;
  onSelectionChange?: (selectedNodeIds: string[]) => void;
};

// Diagram orchestration: root selection, direct subgraph, automatic layout, hover/highlight, and read-only render.
export const ServiceMapDiagram = ({
  nodes,
  edges,
  rootNodeId,
  variant = "focused",
  mode = "viewer",
  interactive = true,
  preservePositions = false,
  height,
  onMapChange,
  onNodeClick,
  onSelectionChange,
}: ServiceMapDiagramProps) => {
  const [rf, setRf] = useState<ReactFlowInstance<BusinessServiceNode, ServiceMapEdge> | null>(null);
  const [hoveredNodeId, setHoveredNodeId] = useState<string | null>(null);
  const [focusedEdgeId, setFocusedEdgeId] = useState<string | null>(null);
  const [editorNodes, setEditorNodes] = useState<BusinessServiceNode[]>([]);
  const [editorEdges, setEditorEdges] = useState<ServiceMapEdge[]>([]);
  const [editorReady, setEditorReady] = useState(false);
  const lastEmittedSnapshotRef = React.useRef<string>("");
  const lastSyncedPropsSnapshotRef = React.useRef<string>("");
  const skipNextEmitRef = React.useRef(false);
  const isEditor = mode === "editor" && variant === "full";
  const onInit: OnInit<BusinessServiceNode, ServiceMapEdge> = (instance) => {
    setRf(instance);
  };

  const activeRootId = useMemo(() => {
    if (variant === "full" || isEditor) {
      return undefined;
    }
    if (!nodes.length) {
      return undefined;
    }
    if (rootNodeId && nodes.some((n) => n.id === rootNodeId)) {
      return rootNodeId;
    }
    return nodes[0].id;
  }, [isEditor, nodes, rootNodeId, variant]);

  const viewerGraphBase = useMemo(() => {
    if (variant === "full") {
      const validEdges = filterEdgesForKnownNodes(nodes, edges);
      if (preservePositions) {
        return {
          nodes: withDiagramNodeDefaults(nodes),
          edges: validEdges,
        };
      }
      return {
        nodes: layoutFullGraph(nodes, validEdges),
        edges: validEdges,
      };
    }

    if (!activeRootId) {
      return {
        nodes: [] as BusinessServiceNode[],
        edges: [] as ServiceMapEdge[],
      };
    }

    const connected = buildConnectedSubgraph(activeRootId, nodes, edges);
    const laidOutNodes = layoutAroundRoot(activeRootId, connected.nodes, connected.edges);

    return {
      nodes: laidOutNodes,
      edges: connected.edges,
    };
  }, [activeRootId, edges, nodes, preservePositions, variant]);

  const editorInitialGraph = useMemo(() => {
    if (!isEditor) {
      return {
        nodes: [] as BusinessServiceNode[],
        edges: [] as ServiceMapEdge[],
      };
    }

    const normalizedNodes = withDiagramNodeDefaults(nodes);
    const normalizedEdges = normalizeEditableEdges(
      filterEdgesForKnownNodes(normalizedNodes, edges),
      normalizedNodes,
    );

    return {
      nodes: normalizedNodes,
      edges: normalizedEdges,
    };
  }, [edges, isEditor, nodes]);
  const editorInitialSnapshot = useMemo(
    () =>
      serializeDiagramSnapshot(editorInitialGraph.nodes, editorInitialGraph.edges),
    [editorInitialGraph.edges, editorInitialGraph.nodes],
  );

  useEffect(() => {
    if (!isEditor) {
      setEditorReady(false);
      lastEmittedSnapshotRef.current = "";
      lastSyncedPropsSnapshotRef.current = "";
      skipNextEmitRef.current = false;
      return;
    }

    if (editorInitialSnapshot === lastSyncedPropsSnapshotRef.current) {
      if (!editorReady) {
        setEditorReady(true);
      }
      return;
    }
    lastSyncedPropsSnapshotRef.current = editorInitialSnapshot;

    const currentSnapshot = serializeDiagramSnapshot(editorNodes, editorEdges);
    if (currentSnapshot === editorInitialSnapshot) {
      if (!editorReady) {
        setEditorReady(true);
      }
      return;
    }

    skipNextEmitRef.current = true;
    setEditorNodes(editorInitialGraph.nodes);
    setEditorEdges(editorInitialGraph.edges);
    lastEmittedSnapshotRef.current = editorInitialSnapshot;
    if (!editorReady) {
      setEditorReady(true);
    }
  }, [
    editorEdges,
    editorInitialGraph.edges,
    editorInitialGraph.nodes,
    editorInitialSnapshot,
    editorNodes,
    editorReady,
    isEditor,
  ]);

  const graphBase = isEditor
    ? {
        nodes: editorNodes,
        edges: editorEdges,
      }
    : viewerGraphBase;

  const onNodesChange = React.useCallback(
    (changes: NodeChange<BusinessServiceNode>[]) => {
      if (!isEditor) {
        return;
      }
      setEditorNodes((current) => applyNodeChanges(changes, current));
    },
    [isEditor],
  );

  const onEdgesChange = React.useCallback(
    (changes: EdgeChange<ServiceMapEdge>[]) => {
      if (!isEditor) {
        return;
      }
      setEditorEdges((current) =>
        normalizeEditableEdges(
          applyEdgeChanges(changes, current),
          editorNodes,
        ),
      );
    },
    [editorNodes, isEditor],
  );

  const onConnect = React.useCallback(
    (connection: Connection) => {
      if (!isEditor || !connection.source || !connection.target) {
        return;
      }
      if (connection.source === connection.target) {
        return;
      }

      const edge = {
        id: buildEdgeId(connection.source, connection.target),
        source: connection.source,
        target: connection.target,
      };

      setEditorEdges((current) =>
        normalizeEditableEdges(addEdge(edge, current), editorNodes),
      );
    },
    [editorNodes, isEditor],
  );

  useEffect(() => {
    if (!isEditor || !onMapChange || !editorReady) {
      return;
    }
    if (skipNextEmitRef.current) {
      skipNextEmitRef.current = false;
      return;
    }

    const nextNodes = editorNodes.map((node) => ({
      ...node,
      position: {
        x: Number(node.position.x.toFixed(2)),
        y: Number(node.position.y.toFixed(2)),
      },
    }));
    const nextEdges = normalizeEditableEdges(editorEdges, editorNodes);
    const nextSnapshot = serializeDiagramSnapshot(nextNodes, nextEdges);
    if (lastEmittedSnapshotRef.current === nextSnapshot) {
      return;
    }
    lastEmittedSnapshotRef.current = nextSnapshot;

    onMapChange({
      nodes: nextNodes,
      edges: nextEdges,
    });
  }, [editorEdges, editorNodes, editorReady, isEditor, onMapChange]);

  useEffect(() => {
    if (!onSelectionChange) {
      return;
    }

    if (!isEditor || !interactive) {
      onSelectionChange([]);
      return;
    }

    const selectedNodeIds = graphBase.nodes
      .filter((node) => node.selected)
      .map((node) => node.id);

    onSelectionChange(selectedNodeIds);
  }, [graphBase.nodes, interactive, isEditor, onSelectionChange]);

  const styledEdges = useMemo(
    () =>
      styleEdges(
        graphBase.edges,
        graphBase.nodes,
        hoveredNodeId,
        focusedEdgeId,
        isEditor ? "editor" : "viewer",
      ),
    [focusedEdgeId, graphBase.edges, graphBase.nodes, hoveredNodeId, isEditor],
  );

  useEffect(() => {
    if (!focusedEdgeId) {
      return;
    }
    const exists = graphBase.edges.some((edge) => edge.id === focusedEdgeId);
    if (!exists) {
      setFocusedEdgeId(null);
    }
  }, [focusedEdgeId, graphBase.edges]);
  const connectedNodeIds = useMemo(() => {
    if (isEditor) {
      return new Set<string>();
    }
    if (!hoveredNodeId) {
      return new Set<string>();
    }

    const ids = new Set<string>();
    for (const edge of graphBase.edges) {
      if (edge.source === hoveredNodeId) {
        ids.add(edge.target);
      } else if (edge.target === hoveredNodeId) {
        ids.add(edge.source);
      }
    }
    return ids;
  }, [graphBase.edges, hoveredNodeId, isEditor]);

  const diagramHeight = useMemo(() => {
    if (!graphBase.nodes.length) {
      return DIAGRAM_MIN_HEIGHT;
    }

    let minY = Number.POSITIVE_INFINITY;
    let maxY = Number.NEGATIVE_INFINITY;

    for (const node of graphBase.nodes) {
      const top = node.position.y;
      const bottom = node.position.y + NODE_HEIGHT;
      if (top < minY) {
        minY = top;
      }
      if (bottom > maxY) {
        maxY = bottom;
      }
    }

    return Math.max(DIAGRAM_MIN_HEIGHT, Math.ceil(maxY - minY + DIAGRAM_PADDING_Y * 2));
  }, [graphBase.nodes]);

  useEffect(() => {
    if (!rf || !graphBase.nodes.length || isEditor) {
      return;
    }
    rf.fitView(FIT_VIEW_OPTIONS);
  }, [rf, graphBase.nodes, isEditor]);

  useEffect(() => {
    if (!rf || !isEditor || !editorInitialGraph.nodes.length) {
      return;
    }
    rf.fitView(FIT_VIEW_OPTIONS);
  }, [editorInitialGraph.nodes.length, isEditor, rf]);

  const nodeTypes = useMemo(
    () => ({
      ServiceNode: (props: NodeProps<BusinessServiceNode>) => (
        <ServiceNode
          {...props}
          rootNodeId={activeRootId}
          hoveredNodeId={hoveredNodeId}
          connectedNodeIds={connectedNodeIds}
          mode={isEditor ? "editor" : "viewer"}
          onNodeActionClick={isEditor && interactive ? onNodeClick : undefined}
        />
      ),
    }),
    [activeRootId, connectedNodeIds, hoveredNodeId, interactive, isEditor, onNodeClick],
  );

  return (
    <Box
      sx={{
        width: "100%",
        height: height ?? diagramHeight,
        overflow: "hidden",
        position: "relative",
        pointerEvents: interactive ? "auto" : "none",
      }}
    >
      <ReactFlow<BusinessServiceNode, ServiceMapEdge>
        onInit={onInit}
        nodes={graphBase.nodes}
        edges={styledEdges}
        nodeTypes={nodeTypes}
        fitView={!isEditor}
        fitViewOptions={FIT_VIEW_OPTIONS}
        nodesDraggable={isEditor && interactive}
        nodesConnectable={isEditor && interactive}
        elementsSelectable={isEditor && interactive}
        panOnDrag={isEditor && interactive}
        zoomOnScroll={isEditor && interactive}
        zoomOnPinch={isEditor && interactive}
        zoomOnDoubleClick={isEditor && interactive}
        selectionOnDrag={isEditor && interactive}
        deleteKeyCode={isEditor && interactive ? ["Delete", "Backspace"] : []}
        onNodesChange={isEditor ? onNodesChange : undefined}
        onEdgesChange={isEditor ? onEdgesChange : undefined}
        onConnect={isEditor ? onConnect : undefined}
        onEdgeDoubleClick={
          isEditor
            ? (_, edge) => {
                setEditorEdges((current) =>
                  current.filter((currentEdge) => currentEdge.id !== edge.id),
                );
              }
            : undefined
        }
        onNodeMouseEnter={(_, node) => {
          if (!isEditor && interactive) {
            setHoveredNodeId(node.id);
          }
        }}
        onNodeMouseLeave={() => {
          if (!isEditor && interactive) {
            setHoveredNodeId(null);
          }
        }}
        onPaneClick={interactive ? () => {
          setHoveredNodeId(null);
          setFocusedEdgeId(null);
        } : undefined}
        onEdgeClick={
          interactive
            ? (_, edge) => {
                setFocusedEdgeId(edge.id);
              }
            : undefined
        }
        onNodeClick={(_, node) => {
          setFocusedEdgeId(null);
          if (!isEditor && interactive && onNodeClick) {
            const clickPosition = getClientPointFromEvent(_);
            const currentTarget =
              _ && typeof _ === "object" && "currentTarget" in _
                ? (_ as { currentTarget?: EventTarget | null }).currentTarget
                : null;
            onNodeClick(node.id, {
              left: clickPosition?.left ?? window.innerWidth / 2,
              top: clickPosition?.top ?? window.innerHeight / 2,
              anchorEl: currentTarget instanceof HTMLElement ? currentTarget : null,
            });
          }
        }}
        proOptions={{ hideAttribution: true }}
      />
    </Box>
  );
};
